import React, { useState, useMemo } from 'react';
import { Ico, COLOR_HEX } from './icons';

// ─── Mock library data ────────────────────────────────────────────
// Representative set so the Documents surface feels real. Each doc captures
// enough metadata to render an at-a-glance cover thumbnail.
const LIBRARY = [
  {
    id:'d1', name:'C172SP Kneeboard', aircraft:'N12345 · C172SP',
    category:'Checklists', updated:'2 min ago', updatedSort: 1,
    pages:8, tabs:8, starred:true, exportable:false, color:'blue',
    cover:{ orientation:'portrait',
      rails:[
        {side:'left',  tabs:[
          {label:'PREFLT', color:'blue'},
          {label:'DEPART', color:'green'},
          {label:'ENRTE',  color:'teal'},
          {label:'ARRIVE', color:'purple'},
        ]},
        {side:'right', tabs:[
          {label:'EMERG',color:'red'},
          {label:'PERF', color:'orange'},
        ]},
        {side:'bottom',tabs:[
          {label:'W&B', color:'yellow'},
          {label:'FREQ',color:'gray'},
        ]},
      ]}
  },
  {
    id:'d2', name:'SR22 Before Start & Taxi', aircraft:'N882PC · SR22',
    category:'Checklists', updated:'1 hr ago', updatedSort: 60,
    pages:4, tabs:4, starred:false, exportable:false, color:'teal',
    cover:{ orientation:'portrait',
      rails:[
        {side:'left', tabs:[
          {label:'PREFLT',color:'blue'},
          {label:'START', color:'teal'},
          {label:'TAXI',  color:'green'},
          {label:'RUNUP', color:'yellow'},
        ]},
      ]}
  },
  {
    id:'d3', name:'IFR Emergency Procedures', aircraft:'Any type · IFR',
    category:'Emergency', updated:'Yesterday', updatedSort: 24*60,
    pages:12, tabs:5, starred:true, exportable:false, color:'red',
    cover:{ orientation:'portrait',
      rails:[
        {side:'left', tabs:[
          {label:'ENGINE',color:'red'},
          {label:'ELEC',  color:'red'},
          {label:'VAC',   color:'red'},
          {label:'FIRE',  color:'red'},
          {label:'COMM',  color:'red'},
        ]},
      ]}
  },
  {
    id:'d4', name:'KPAO Local Procedures', aircraft:'KPAO · Palo Alto',
    category:'Reference', updated:'2 days ago', updatedSort: 2*24*60,
    pages:9, tabs:3, starred:false, exportable:false, color:'purple',
    cover:{ orientation:'landscape',
      rails:[
        {side:'top', tabs:[
          {label:'ARR',  color:'purple'},
          {label:'DEP',  color:'magenta'},
          {label:'GND',  color:'teal'},
        ]},
      ]}
  },
  {
    id:'d5', name:'Weight & Balance — SR22', aircraft:'N882PC · SR22',
    category:'Performance', updated:'3 days ago', updatedSort: 3*24*60,
    pages:3, tabs:2, starred:false, exportable:false, color:'green',
    cover:{ orientation:'portrait',
      rails:[
        {side:'right', tabs:[
          {label:'W&B',  color:'green'},
          {label:'LOAD', color:'green'},
        ]},
      ]}
  },
  {
    id:'d6', name:'Instrument Approach Briefing', aircraft:'IFR briefing',
    category:'Reference', updated:'5 days ago', updatedSort: 5*24*60,
    pages:6, tabs:4, starred:true, exportable:false, color:'blue',
    cover:{ orientation:'portrait',
      rails:[
        {side:'left', tabs:[
          {label:'BRIEF', color:'blue'},
          {label:'PLATE', color:'magenta'},
          {label:'MISS',  color:'yellow'},
          {label:'HOLD',  color:'gray'},
        ]},
      ]}
  },
  {
    id:'d7', name:'Night VFR Cross-Country', aircraft:'N12345 · C172S',
    category:'Flight log', updated:'1 wk ago', updatedSort: 7*24*60,
    pages:5, tabs:3, starred:false, exportable:false, color:'gray',
    cover:{ orientation:'portrait',
      rails:[
        {side:'left', tabs:[
          {label:'LEGS', color:'gray'},
          {label:'FUEL', color:'yellow'},
          {label:'DIV',  color:'red'},
        ]},
      ]}
  },
  {
    id:'d8', name:'Piper Archer III Memory Items', aircraft:'N4567P · PA-28',
    category:'Emergency', updated:'2 wks ago', updatedSort: 14*24*60,
    pages:2, tabs:3, starred:false, exportable:false, color:'red',
    cover:{ orientation:'portrait',
      rails:[
        {side:'left', tabs:[
          {label:'BOLDFACE', color:'red'},
          {label:'EMERG',    color:'red'},
          {label:'MAYDAY',   color:'red'},
        ]},
      ]}
  },
  {
    id:'d9', name:'Mountain Flying Notes', aircraft:'Technique reference',
    category:'Reference', updated:'1 mo ago', updatedSort: 30*24*60,
    pages:8, tabs:4, starred:false, exportable:false, color:'teal',
    cover:{ orientation:'landscape',
      rails:[
        {side:'top', tabs:[
          {label:'CLIMB', color:'teal'},
          {label:'WIND',  color:'teal'},
          {label:'PASS',  color:'yellow'},
          {label:'OUT',   color:'red'},
        ]},
      ]}
  },
];

const CATEGORIES = ['All', 'Checklists', 'Emergency', 'Performance', 'Reference', 'Flight log'];

// ─── Document cover thumbnail ──────────────────────────────────────
// Renders a mini of the actual page + rails so pilots recognize their
// docs by spatial shape (where the tabs are), not just title text.
function DocCover({ cover, category, name }) {
  const orient = cover.orientation || 'portrait';
  const bySide = { top:[], bottom:[], left:[], right:[] };
  (cover.rails || []).forEach(r => { bySide[r.side] = r.tabs || []; });

  const renderRail = (side) => {
    const tabs = bySide[side];
    if (!tabs.length) return null;
    return (
      <div className={`dc-rail dc-rail-${side}`}>
        {tabs.map((t,i)=>(
          <div key={i} className={`dc-chip dc-c-${t.color}`}>{t.label}</div>
        ))}
      </div>
    );
  };

  return (
    <div className={`dc-cover dc-${orient}`}>
      {renderRail('top')}
      <div className="dc-mid">
        {renderRail('left')}
        <div className="dc-content">
          <div className="dc-title-line">{name}</div>
          <div className="dc-line"/>
          <div className="dc-line short"/>
          <div className="dc-line"/>
          <div className="dc-line"/>
          <div className="dc-line mid"/>
          <div className="dc-line"/>
          <div className="dc-line short"/>
        </div>
        {renderRail('right')}
      </div>
      {renderRail('bottom')}
    </div>
  );
}

// ─── A single card ────────────────────────────────────────────────
function DocCard({ doc, selected, onSelect, onOpen, onStar, onDuplicate, onPublish, onExport, onDelete, onRename }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className={`doc-card ${selected?'selected':''}`}>
      <div className="dc-top" onClick={onOpen} role="button" tabIndex={0}>
        <DocCover cover={doc.cover} category={doc.category} name={doc.name}/>
        <button className={`dc-star ${doc.starred?'on':''}`}
                onClick={(e)=>{e.stopPropagation(); onStar();}}
                title={doc.starred?'Unstar':'Star'}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill={doc.starred?'currentColor':'none'} stroke="currentColor" strokeWidth="1.3">
            <path d="M8 1.5l2 4.2 4.5.7-3.3 3.2.8 4.5L8 11.9l-4 2.2.8-4.5L1.5 6.4l4.5-.7L8 1.5z"/>
          </svg>
        </button>
        <label className="dc-check" onClick={(e)=>e.stopPropagation()}>
          <input type="checkbox" checked={selected} onChange={onSelect}/>
          <span/>
        </label>
      </div>
      <div className="dc-meta">
        <div className="dc-title-row">
          <div className="dc-name" onClick={onOpen}>{doc.name}</div>
          <button className="dc-menu-btn" onClick={()=>setMenuOpen(o=>!o)}><Ico.More width="14" height="14"/></button>
          {menuOpen && (
            <>
              <div className="dc-menu-scrim" onClick={()=>setMenuOpen(false)}/>
              <div className="dc-menu">
                <button onClick={()=>{setMenuOpen(false); onOpen();}}><Ico.Edit width="12" height="12"/> Open</button>
                <button onClick={()=>{setMenuOpen(false); onRename();}}>✎ Rename</button>
                <button onClick={()=>{setMenuOpen(false); onDuplicate();}}><Ico.Copy width="12" height="12"/> Duplicate</button>
                <button onClick={()=>{setMenuOpen(false); onExport();}}><Ico.Download width="12" height="12"/> Export PDF</button>
                <div className="dc-menu-sep"/>
                <button className="danger" onClick={()=>{setMenuOpen(false); onDelete();}}><Ico.Trash width="12" height="12"/> Delete</button>
              </div>
            </>
          )}
        </div>
        <div className="dc-sub">{doc.aircraft}</div>
        <div className="dc-stats">
          <span className={`dc-cat dc-cat-${doc.category.replace(/\s+/g,'-').toLowerCase()}`}>{doc.category}</span>
          <span className="dc-stat"><Ico.File width="11" height="11"/>{doc.pages} pages</span>
          <span className="dc-stat">{doc.tabs} tabs</span>
          <span className="dc-stat dc-updated">{doc.updated}</span>
        </div>
      </div>
    </div>
  );
}

// ─── A single row (list view) ──────────────────────────────────────
function DocRow({ doc, selected, onSelect, onOpen, onStar, onDuplicate, onPublish, onExport, onDelete, onRename }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className={`doc-row ${selected?'selected':''}`}>
      <label className="dr-check" onClick={(e)=>e.stopPropagation()}>
        <input type="checkbox" checked={selected} onChange={onSelect}/>
        <span/>
      </label>
      <button className={`dr-star ${doc.starred?'on':''}`} onClick={onStar} title={doc.starred?'Unstar':'Star'}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill={doc.starred?'currentColor':'none'} stroke="currentColor" strokeWidth="1.3">
          <path d="M8 1.5l2 4.2 4.5.7-3.3 3.2.8 4.5L8 11.9l-4 2.2.8-4.5L1.5 6.4l4.5-.7L8 1.5z"/>
        </svg>
      </button>
      <div className="dr-mini"><DocCover cover={doc.cover} category={doc.category} name={doc.name}/></div>
      <div className="dr-title-cell" onClick={onOpen}>
        <div className="dr-name">{doc.name}</div>
        <div className="dr-sub">{doc.aircraft}</div>
      </div>
      <div className={`dr-cat dc-cat-${doc.category.replace(/\s+/g,'-').toLowerCase()}`}>{doc.category}</div>
      <div className="dr-stat">{doc.pages}</div>
      <div className="dr-stat">{doc.tabs}</div>
      <div className="dr-updated">{doc.updated}</div>
      <div className="dr-actions">
        <button className="dr-act" onClick={onOpen} title="Open"><Ico.Edit width="14" height="14"/></button>
        <button className="dr-act" onClick={onExport} title="Export PDF"><Ico.Download width="14" height="14"/></button>
        <button className="dr-act" onClick={()=>setMenuOpen(o=>!o)} title="More"><Ico.More width="14" height="14"/></button>
        {menuOpen && (
          <>
            <div className="dc-menu-scrim" onClick={()=>setMenuOpen(false)}/>
            <div className="dc-menu dr-menu">
              <button onClick={()=>{setMenuOpen(false); onRename();}}>✎ Rename</button>
              <button onClick={()=>{setMenuOpen(false); onDuplicate();}}><Ico.Copy width="12" height="12"/> Duplicate</button>
              <div className="dc-menu-sep"/>
              <button className="danger" onClick={()=>{setMenuOpen(false); onDelete();}}><Ico.Trash width="12" height="12"/> Delete</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── The Documents surface ─────────────────────────────────────────
export function DocumentsView({ onOpenDoc, onNewDoc, onToast }) {
  const [library, setLibrary] = useState(LIBRARY);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [starredOnly, setStarredOnly] = useState(false);
  const [sort, setSort] = useState('recent'); // 'recent' | 'name' | 'pages'
  const [view, setView] = useState('grid'); // 'grid' | 'list'
  const [selected, setSelected] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionConfirm, setActionConfirm] = useState(null);

  const filtered = useMemo(()=>{
    let arr = library.slice();
    if (category !== 'All') arr = arr.filter(d => d.category === category);
    if (starredOnly) arr = arr.filter(d => d.starred);
    if (query.trim()) {
      const q = query.toLowerCase();
      arr = arr.filter(d =>
        d.name.toLowerCase().includes(q) ||
        d.aircraft.toLowerCase().includes(q) ||
        d.category.toLowerCase().includes(q)
      );
    }
    if (sort === 'recent') arr.sort((a,b)=>a.updatedSort - b.updatedSort);
    else if (sort === 'name') arr.sort((a,b)=>a.name.localeCompare(b.name));
    else if (sort === 'pages') arr.sort((a,b)=>b.pages - a.pages);
    return arr;
  }, [library, query, category, starredOnly, sort]);

  const toggleSel = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set(filtered.map(d=>d.id)));

  const toggleStar = (id) => setLibrary(lib => lib.map(d => d.id===id ? {...d, starred:!d.starred} : d));
  const duplicateDoc = (id) => {
    setLibrary(lib => {
      const src = lib.find(d=>d.id===id);
      if (!src) return lib;
      const dup = {...src, id:'d'+Math.random().toString(36).slice(2,7), name:src.name+' (copy)', updated:'just now', updatedSort:0, starred:false, exportable:false};
      return [dup, ...lib];
    });
    onToast && onToast('Document duplicated');
  };
  const deleteDoc = (id) => {
    const doc = library.find(d => d.id === id);
    if (doc) setDeleteConfirm({ type: 'single', id, name: doc.name });
  };
  const confirmDeleteAction = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === 'single') {
      setLibrary(lib => lib.filter(d => d.id !== deleteConfirm.id));
      setSelected(s => { const n = new Set(s); n.delete(deleteConfirm.id); return n; });
      onToast && onToast('Moved to trash', { type: 'info' });
    } else {
      setLibrary(lib => lib.filter(d => !selected.has(d.id)));
      onToast && onToast(`${selected.size} document${selected.size === 1 ? '' : 's'} moved to trash`, { type: 'info' });
      clearSel();
    }
    setDeleteConfirm(null);
  };
  const renameDoc = (id) => {
    const d = library.find(x=>x.id===id); if (!d) return;
    const name = prompt('Rename document', d.name);
    if (name && name.trim()) {
      setLibrary(lib => lib.map(x => x.id===id ? {...x, name:name.trim(), updated:'just now', updatedSort:0} : x));
    }
  };
  const publishDoc = (id) => {
    const doc = library.find(d => d.id === id);
    if (doc) setActionConfirm({ type: 'publish', id, name: doc.name });
  };
  const exportDoc = (id) => {
    const doc = library.find(d => d.id === id);
    if (doc) setActionConfirm({ type: 'export', id, name: doc.name });
  };
  const confirmAction = () => {
    if (!actionConfirm) return;
    if (actionConfirm.type === 'publish') {
      setLibrary(lib => lib.map(d => d.id === actionConfirm.id ? { ...d, exportable: true } : d));
      onToast && onToast('Document published to community');
    } else {
      onToast && onToast('Export started', { type: 'info' });
    }
    setActionConfirm(null);
  };

  const bulkDelete = () => {
    setDeleteConfirm({ type: 'bulk', count: selected.size });
  };
  const bulkExport = () => {
    onToast && onToast(`Exporting ${selected.size} document${selected.size===1?'':'s'}`, { type: 'info' });
    clearSel();
  };
  const bulkStar = () => {
    setLibrary(lib => lib.map(d => selected.has(d.id) ? {...d, starred:true} : d));
    clearSel();
  };

  const countsByCategory = useMemo(()=>{
    const c = { All: library.length };
    library.forEach(d => { c[d.category] = (c[d.category]||0) + 1; });
    return c;
  }, [library]);

  return (
    <div className="docs-view">
      {/* Hero / page header */}
      <div className="docs-hero">
        <div className="docs-hero-main">
          <div className="docs-crumb">Library</div>
          <h1 className="lib-section-title ty-heading-lg">My documents</h1>
          <div className="docs-summary">
            <span><strong>{library.length}</strong> documents</span>
            <span className="sep">·</span>
            <span><strong>{library.reduce((s,d)=>s+d.pages,0)}</strong> pages</span>
            <span className="sep">·</span>
            <span><strong>{library.filter(d=>d.starred).length}</strong> starred</span>
          </div>
        </div>
        <div className="docs-hero-actions">
          <button className="btn btn--ghost btn--md" onClick={()=>onToast && onToast('Import coming soon', { type: 'info' })}>
            <Ico.Upload width="14" height="14"/> Import PDF
          </button>
          <button className="btn btn--primary btn--md" onClick={onNewDoc}>
            <Ico.Plus/> Create new document
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="docs-toolbar">
        <div className="docs-search">
          <Ico.Search width="14" height="14"/>
          <input type="text" placeholder="Search by name, aircraft, or category…" value={query} onChange={e=>setQuery(e.target.value)}/>
          {query && <button className="ds-clear" onClick={()=>setQuery('')}>×</button>}
        </div>

        <div className="docs-toolbar-right">
          <label className={`docs-star-filter ${starredOnly?'on':''}`}>
            <input type="checkbox" checked={starredOnly} onChange={e=>setStarredOnly(e.target.checked)}/>
            <svg width="12" height="12" viewBox="0 0 16 16" fill={starredOnly?'currentColor':'none'} stroke="currentColor" strokeWidth="1.3">
              <path d="M8 1.5l2 4.2 4.5.7-3.3 3.2.8 4.5L8 11.9l-4 2.2.8-4.5L1.5 6.4l4.5-.7L8 1.5z"/>
            </svg>
            Starred
          </label>

          <div className="docs-sort">
            <span className="ds-lbl">Sort</span>
            <select value={sort} onChange={e=>setSort(e.target.value)}>
              <option value="recent">Most recent</option>
              <option value="name">Name A–Z</option>
              <option value="pages">Most pages</option>
            </select>
          </div>

          <div className="docs-view-switch">
            <button className={view==='grid'?'on':''} onClick={()=>setView('grid')} title="Grid">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <rect x="2" y="2" width="5" height="5"/><rect x="9" y="2" width="5" height="5"/>
                <rect x="2" y="9" width="5" height="5"/><rect x="9" y="9" width="5" height="5"/>
              </svg>
            </button>
            <button className={view==='list'?'on':''} onClick={()=>setView('list')} title="List">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <rect x="2" y="3" width="12" height="2"/>
                <rect x="2" y="7" width="12" height="2"/>
                <rect x="2" y="11" width="12" height="2"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Category strip */}
      <div className="docs-cats">
        {CATEGORIES.map(c => (
          <button key={c} className={`docs-cat ${category===c?'on':''}`} onClick={()=>setCategory(c)}>
            {c}
            <span className="ct">{countsByCategory[c] || 0}</span>
          </button>
        ))}
      </div>

      {/* Bulk-action bar (when selection) */}
      {selected.size > 0 && (
        <div className="docs-bulk">
          <div className="docs-bulk-info">
            <strong>{selected.size}</strong> selected
            <button className="lnk" onClick={selectAll}>Select all {filtered.length}</button>
            <button className="lnk" onClick={clearSel}>Clear</button>
          </div>
          <div className="docs-bulk-actions">
            <button className="btn btn--ghost btn--md" onClick={bulkStar}>Star</button>
            <button className="btn btn--ghost btn--md" onClick={bulkExport}><Ico.Download width="14" height="14"/> Export selected</button>
            <button className="btn btn--danger-ghost btn--md" onClick={bulkDelete}><Ico.Trash width="14" height="14"/> Delete</button>
          </div>
        </div>
      )}

      {/* Results */}
      {filtered.length === 0 ? (
        <div className="docs-empty">
          <div className="docs-empty-art">
            <svg width="72" height="72" viewBox="0 0 80 80" fill="none" stroke="currentColor" strokeWidth="1.2">
              <rect x="18" y="14" width="44" height="56"/>
              <rect x="14" y="18" width="44" height="56" fill="#fff"/>
              <path d="M22 28h28M22 34h28M22 40h20M22 46h24"/>
              <rect x="10" y="22" width="4" height="10" fill="#445468"/>
              <rect x="10" y="36" width="4" height="10" fill="#B54434"/>
              <rect x="10" y="50" width="4" height="10" fill="#C4A24A"/>
            </svg>
          </div>
          <div className="docs-empty-title">{query ? 'No matches' : category!=='All' ? `No ${category.toLowerCase()} yet` : 'Your library is empty'}</div>
          <div className="docs-empty-sub">
            {query ? 'Try a different name or clear filters.' : 'Create a new document or import an existing PDF to get started.'}
          </div>
          <div className="docs-empty-actions">
            {query && <button className="btn btn--ghost btn--md" onClick={()=>{setQuery(''); setCategory('All'); setStarredOnly(false);}}>Clear filters</button>}
            <button className="btn btn--primary btn--md" onClick={onNewDoc}><Ico.Plus/> Create new document</button>
          </div>
        </div>
      ) : view === 'grid' ? (
        <div className="docs-grid">
          {/* Always-present new-doc tile */}
          <button className="doc-new-tile" onClick={onNewDoc}>
            <div className="dnt-circle"><Ico.Plus width="18" height="18"/></div>
            <div className="dnt-label">Create new document</div>
            <div className="dnt-sub">Start blank or from a template</div>
          </button>
          {filtered.map(d => (
            <DocCard key={d.id} doc={d}
              selected={selected.has(d.id)}
              onSelect={()=>toggleSel(d.id)}
              onOpen={()=>onOpenDoc(d)}
              onStar={()=>toggleStar(d.id)}
              onDuplicate={()=>duplicateDoc(d.id)}
              onPublish={()=>publishDoc(d.id)}
              onExport={()=>exportDoc(d.id)}
              onDelete={()=>deleteDoc(d.id)}
              onRename={()=>renameDoc(d.id)}/>
          ))}
        </div>
      ) : (
        <div className="docs-list">
          <div className="doc-row doc-row-head">
            <label className="dr-check">
              <input type="checkbox"
                     checked={filtered.length>0 && filtered.every(d=>selected.has(d.id))}
                     ref={el=>{ if(el) el.indeterminate = selected.size>0 && !filtered.every(d=>selected.has(d.id)); }}
                     onChange={e=> e.target.checked ? selectAll() : clearSel()}/>
              <span/>
            </label>
            <div/>
            <div/>
            <div className="dr-h">Name</div>
            <div className="dr-h">Category</div>
            <div className="dr-h">Pages</div>
            <div className="dr-h">Tabs</div>
            <div className="dr-h">Updated</div>
            <div/>
          </div>
          {filtered.map(d => (
            <DocRow key={d.id} doc={d}
              selected={selected.has(d.id)}
              onSelect={()=>toggleSel(d.id)}
              onOpen={()=>onOpenDoc(d)}
              onStar={()=>toggleStar(d.id)}
              onDuplicate={()=>duplicateDoc(d.id)}
              onPublish={()=>publishDoc(d.id)}
              onExport={()=>exportDoc(d.id)}
              onDelete={()=>deleteDoc(d.id)}
              onRename={()=>renameDoc(d.id)}/>
          ))}
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-backdrop" onClick={() => setDeleteConfirm(null)}>
          <div className="modal modal--sm" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h2 className="modal__title">
                {deleteConfirm.type === 'single'
                  ? `Delete "${deleteConfirm.name}"?`
                  : `Delete ${deleteConfirm.count} documents?`}
              </h2>
              <button className="btn btn--icon" onClick={() => setDeleteConfirm(null)}><Ico.Close width="16" height="16"/></button>
            </div>
            <div className="modal__body">
              <p className="confirm-public">This action cannot be undone.</p>
            </div>
            <div className="modal__footer">
              <button className="btn btn--ghost btn--md" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn--danger btn--md" onClick={confirmDeleteAction}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {actionConfirm && (
        <div className="modal-backdrop" onClick={() => setActionConfirm(null)}>
          <div className="modal modal--sm" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h2 className="modal__title">
                {actionConfirm.type === 'publish'
                  ? `Publish "${actionConfirm.name}"?`
                  : `Export "${actionConfirm.name}"?`}
              </h2>
              <button className="btn btn--icon" onClick={() => setActionConfirm(null)}><Ico.Close width="16" height="16"/></button>
            </div>
            <div className="modal__body">
              {actionConfirm.type === 'publish'
                ? <p>Your document will be shared with the community so all pilots can benefit. PDF export becomes available after publishing.</p>
                : <p>The document will be exported as a PDF file and downloaded to your device.</p>
              }
            </div>
            <div className="modal__footer">
              <button className="btn btn--ghost btn--md" onClick={() => setActionConfirm(null)}>Cancel</button>
              <button className="btn btn--primary btn--md" onClick={confirmAction}>
                {actionConfirm.type === 'publish' ? 'Publish' : 'Export'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
